import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Calendar, CheckCircle, Clock, AlertCircle, CheckSquare, Activity, Target, Eye } from 'lucide-react';
import { apiService, ControlItem, Facility } from '../services/api';
import { useAuthStore } from '../store';

const DailyChecks: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<ControlItem[]>([]);
  const [allDailyItems, setAllDailyItems] = useState<ControlItem[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [users, setUsers] = useState<Array<{id: number, username: string, role: string}>>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewContent, setViewContent] = useState<{title: string, content: string}>({title: '', content: ''});
  const [formData, setFormData] = useState({
    title: '',
    workDone: '',
    plannedDate: '',
    completedDate: '',
    completionDate: '',
    description: '',
    user: user?.username || '',
    facilityId: '',
    status: ''
  });
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      // Admin ise tüm işleri, değilse sadece kendi işlerini getir
      const isAdmin = user?.role === 'admin';
      const [data, facilitiesData, usersData] = await Promise.all([
        apiService.getControlItems({ 
          period: 'Günlük',
          user: isAdmin ? undefined : user?.username
        }),
        apiService.getFacilities(),
        apiService.getUserOptions()
      ]);
      
      // Kullanıcı bazlı filtreleme - Admin değilse sadece kendi işlerini göster
      let filteredData = data;
      if (!isAdmin && user?.username) {
        filteredData = data.filter(item => 
          item.user_name === user.username || item.user === user.username
        );
      }
      
      // Aktif işleri filtrele (Beklemede ve İşlemde olanlar)
      const activeItems = filteredData.filter(item => 
        item.status === 'Beklemede' || item.status === 'İşlemde'
      );
      
      setItems(activeItems);
      setAllDailyItems(filteredData); // Filtrelenmiş günlük işleri setle
      setFacilities(facilitiesData);
      setUsers(usersData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchItems();
    }
  }, [user]);

  const handleEdit = (index: number) => {
    const item = items[index];
    setFormData({
      title: item.title || '',
      workDone: item.work_done || '',
      plannedDate: '',
      completedDate: item.date,
      completionDate: item.completion_date || '',
      description: item.description || '',
      user: item.user_name || item.user || '',
      facilityId: item.facility_id?.toString() || '',
      status: item.status || ''
    });
    setEditIndex(index);
    setModalOpen(true);
  };

  const handleDelete = async (index: number) => {
    if (window.confirm('Bu işi silmek istediğinize emin misiniz?')) {
      try {
        const item = items[index];
        console.log('Deleting control item:', item.id);
        await apiService.deleteControlItem(Number(item.id));
        console.log('Control item deleted successfully');
        
        // Verileri yeniden yükle
        await fetchItems();
        
        alert('İş başarıyla silindi!');
      } catch (error) {
        console.error('Delete error:', error);
        alert('Silme işlemi başarısız oldu. Lütfen tekrar deneyin.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newItem = {
      title: formData.title,
      description: formData.description,
      period: 'Günlük' as const,
      date: formData.completedDate || new Date().toISOString().split('T')[0],
      facilityId: formData.facilityId,
      workDone: formData.workDone,
      user: formData.user,
      status: formData.status,
      ...(formData.completionDate || formData.status === 'Tamamlandı' ? { completionDate: formData.completionDate || new Date().toISOString().split('T')[0] } : {})
    };
    
    try {
      if (editIndex !== null) {
        // Tamamlama modal'ından geliyorsa allDailyItems'dan al
        const itemToUpdate = completeModalOpen ? allDailyItems[editIndex] : items[editIndex];
        const updated = await apiService.updateControlItem(Number(itemToUpdate.id), newItem);
        
        // items array'ini güncelle
        const newItems = [...items];
        const itemIndex = newItems.findIndex(item => item.id === updated.id);
        if (itemIndex !== -1) {
          newItems[itemIndex] = updated;
          setItems(newItems);
        }
        
        // allDailyItems'ı da güncelle
        const newAllDailyItems = [...allDailyItems];
        const allIndex = newAllDailyItems.findIndex(item => item.id === updated.id);
        if (allIndex !== -1) {
          newAllDailyItems[allIndex] = updated;
          setAllDailyItems(newAllDailyItems);
        }
        
        setEditIndex(null);
      } else {
        const saved = await apiService.createControlItem(newItem);
        
        // Aktif işleri güncelle (Beklemede ve İşlemde olanlar)
        const updatedItems = [...items, saved];
        setItems(updatedItems);
        
        // Tüm günlük işleri de güncelle
        const updatedAllDailyItems = [...allDailyItems, saved];
        setAllDailyItems(updatedAllDailyItems);
      }
      
      // Form'u temizle
      setFormData({
        title: '',
        workDone: '',
        plannedDate: '',
        completedDate: '',
        completionDate: '',
        description: '',
        user: user?.username || '',
        facilityId: '',
        status: ''
      });
      
      setModalOpen(false);
      setCompleteModalOpen(false);
      
      // Verileri otomatik yenile
      const fetchItems = async () => {
        setLoading(true);
        try {
          // Admin ise tüm işleri, değilse sadece kendi işlerini getir
          const isAdmin = user?.role === 'admin';
          const [data, facilitiesData, usersData] = await Promise.all([
            apiService.getControlItems({ 
              period: 'Günlük',
              user: isAdmin ? undefined : user?.username
            }),
            apiService.getFacilities(),
            apiService.getUserOptions()
          ]);
          
          // Kullanıcı bazlı filtreleme - Admin değilse sadece kendi işlerini göster
          let filteredData = data;
          if (!isAdmin && user?.username) {
            filteredData = data.filter(item => 
              item.user_name === user.username || item.user === user.username
            );
          }
          
          // Aktif işleri filtrele (Beklemede ve İşlemde olanlar)
          const activeItems = filteredData.filter(item => 
            item.status === 'Beklemede' || item.status === 'İşlemde'
          );
          
          setItems(activeItems);
          setAllDailyItems(filteredData); // Filtrelenmiş günlük işleri setle
          setFacilities(facilitiesData);
          setUsers(usersData);
        } finally {
          setLoading(false);
        }
      };
      
      // Verileri yenile
      await fetchItems();
      
      // Başarı mesajı göster
      const message = completeModalOpen ? 'İş başarıyla tamamlandı!' : (editIndex !== null ? 'İş başarıyla güncellendi!' : 'İş başarıyla eklendi!');
      alert(message);
      
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('Kaydetme işlemi başarısız oldu! Lütfen tekrar deneyin.');
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'Tamamlandı':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'İşlemde':
        return <Activity className="w-5 h-5 text-violet-500" />;
      case 'Beklemede':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'Yapılmadı':
        return <AlertCircle className="w-5 h-5 text-rose-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'Tamamlandı':
        return 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border-emerald-300 shadow-emerald-100';
      case 'İşlemde':
        return 'bg-gradient-to-r from-violet-100 to-violet-200 text-violet-800 border-violet-300 shadow-violet-100';
      case 'Beklemede':
        return 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border-amber-300 shadow-amber-100';
      case 'Yapılmadı':
        return 'bg-gradient-to-r from-rose-100 to-rose-200 text-rose-800 border-rose-300 shadow-rose-100';
      default:
        return 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 border-slate-300 shadow-slate-100';
    }
  };

  const getStatusText = (status: string | undefined) => {
    switch (status) {
      case 'Tamamlandı':
        return 'Tamamlandı';
      case 'İşlemde':
        return 'İşlemde';
      case 'Beklemede':
        return 'Beklemede';
      case 'Yapılmadı':
        return 'Yapılmadı';
      default:
        return 'Belirsiz';
    }
  };

  // Kullanıcının kendi işi mi kontrol et
  const isUserOwnItem = (item: ControlItem) => {
    return item.user_name === user?.username || item.user === user?.username;
  };

  // İşi tamamla modal'ını aç
  const handleComplete = (index: number) => {
    const item = allDailyItems[index];
    const today = new Date().toISOString().split('T')[0];
    
    setFormData({
      title: item.title || '',
      workDone: '', // Boş bırak - otomatik metin gelmesin
      plannedDate: item.date,
      completedDate: item.date,
      completionDate: today,
      description: item.description || '',
      user: item.user_name || item.user || '',
      facilityId: item.facility_id?.toString() || '',
      status: 'Tamamlandı'
    });
    setEditIndex(index);
    setCompleteModalOpen(true); // Yeni modal için
  };

  return (
    <div className="h-full space-y-3 overflow-y-auto p-3">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0047BB] to-[#003894] shadow-md shadow-[#0047BB]/30">
            <Calendar className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Günlük İş Programı</h1>
            <p className="text-xs text-slate-500">
              {isAdmin ? 'Tüm kullanıcıların günlük işlerini yönetin' : 'Kendi günlük işlerinizi yönetin'}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditIndex(null);
            const today = new Date().toISOString().split('T')[0];
            setFormData({
              title: '',
              workDone: '',
              plannedDate: today,
              completedDate: today,
              completionDate: '',
              description: '',
              user: user?.username || '',
              facilityId: '',
              status: 'İşlemde'
            });
            setModalOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#0047BB] to-[#009CDE] px-3.5 py-2 text-sm font-semibold text-white shadow-md shadow-[#0047BB]/25 transition hover:brightness-105"
        >
          <Plus className="h-4 w-4" />
          Yeni İş Ekle
        </button>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0047BB] to-[#003894] p-3 text-white shadow-md shadow-[#0047BB]/25">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-white/85">{isAdmin ? 'Toplam İş' : 'Kendi İşlerim'}</p>
              <p className="mt-0.5 text-2xl font-bold">{allDailyItems.length}</p>
            </div>
            <div className="rounded-lg bg-white/20 p-2">
              <Calendar className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#009CDE] to-[#0077b3] p-3 text-white shadow-md shadow-[#009CDE]/25">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-white/85">Tamamlanan</p>
              <p className="mt-0.5 text-2xl font-bold">
                {allDailyItems.filter(item => item.status === 'Tamamlandı').length}
              </p>
            </div>
            <div className="rounded-lg bg-white/20 p-2">
              <CheckCircle className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0047BB] via-[#0070c9] to-[#009CDE] p-3 text-white shadow-md shadow-[#0047BB]/25">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-white/85">Bekleyen + İşlemde</p>
              <p className="mt-0.5 text-2xl font-bold">
                {allDailyItems.filter(item => item.status === 'Beklemede' || item.status === 'İşlemde').length}
              </p>
            </div>
            <div className="rounded-lg bg-white/20 p-2">
              <Activity className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#3d5f9e] to-[#2a4578] p-3 text-white shadow-md">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium text-white/85">Yapılmayan</p>
              <p className="mt-0.5 text-2xl font-bold">
                {allDailyItems.filter(item => item.status === 'Yapılmadı').length}
              </p>
            </div>
            <div className="rounded-lg bg-white/20 p-2">
              <AlertCircle className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* İş Listesi */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0047BB]/20 border-t-[#0047BB]" />
          <span className="ml-3 text-sm text-slate-600">Yükleniyor...</span>
        </div>
      ) : allDailyItems.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-[#0047BB]/10 bg-white shadow-sm">
          <div className="px-4 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0047BB]/10">
              <Calendar className="h-6 w-6 text-[#0047BB]" />
            </div>
            <h3 className="mb-1 text-base font-bold text-slate-800">Henüz aktif iş yok</h3>
            <p className="mb-4 text-sm text-slate-500">Yeni iş ekleyerek başlayın.</p>
            <button
              onClick={() => {
                setEditIndex(null);
                const today = new Date().toISOString().split('T')[0];
                setFormData({
                  title: '',
                  workDone: '',
                  plannedDate: today,
                  completedDate: today,
                  completionDate: '',
                  description: '',
                  user: user?.username || '',
                  facilityId: '',
                  status: ''
                });
                setModalOpen(true);
              }}
              className="rounded-xl bg-gradient-to-r from-[#0047BB] to-[#009CDE] px-4 py-2 text-sm font-semibold text-white shadow-md"
            >
              İlk İşi Ekle
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="max-h-[calc(100vh-220px)] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="sticky top-0 z-10 bg-[#eef3fb]">
                <tr className="border-b border-[#0047BB]/15">
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Kayıt No</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">İş Adı</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Açıklama</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Yapılan İş</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Tarih</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Tamamlanma</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Kullanıcı</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Tesis</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600">Durum</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-slate-600">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {allDailyItems
                  .filter(item => {
                    const statusFilter = item.status === 'Beklemede' || item.status === 'İşlemde';
                    const userFilter = isAdmin || isUserOwnItem(item);
                    return statusFilter && userFilter;
                  })
                  .sort((a, b) => (b.recordNo || 0) - (a.recordNo || 0))
                  .map((item, idx) => (
                  <tr key={item.id} className="transition-colors hover:bg-[#f5f8fd]">
                    <td className="whitespace-nowrap px-3 py-2 font-semibold text-slate-600">
                      {item.recordNo || idx + 1}
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-[160px] truncate font-medium text-slate-900" title={item.title}>
                        {item.title}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-[220px]">
                        {item.description ? (
                          <div className="flex items-start gap-1 rounded-lg border border-[#0047BB]/10 bg-[#eef3fb] px-2 py-1.5">
                            <p className="line-clamp-2 flex-1 text-xs text-slate-700">{item.description}</p>
                            <button
                              onClick={() => {
                                setViewContent({title: 'Açıklama', content: item.description || ''});
                                setViewModalOpen(true);
                              }}
                              className="shrink-0 rounded p-0.5 text-[#0047BB] hover:bg-[#0047BB]/10"
                              title="Tamamını görüntüle"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs italic text-slate-400">Açıklama yok</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-[180px]">
                        {item.work_done ? (
                          <div className="flex items-start gap-1 rounded-lg border border-[#009CDE]/15 bg-[#e8f6fc] px-2 py-1.5">
                            <p className="line-clamp-2 flex-1 text-xs font-medium text-[#0077b3]">{item.work_done}</p>
                            <button
                              onClick={() => {
                                setViewContent({title: 'Yapılan İş', content: item.work_done || ''});
                                setViewModalOpen(true);
                              }}
                              className="shrink-0 rounded p-0.5 text-[#009CDE] hover:bg-[#009CDE]/10"
                              title="Tamamını görüntüle"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs italic text-slate-400">Belirtilmemiş</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                      {new Date(item.date).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                      {item.completion_date ? new Date(item.completion_date).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <span className="block max-w-[90px] truncate" title={String(item.user_name || item.user || 'Belirtilmemiş')}>
                        {item.user_name || item.user || 'Belirtilmemiş'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="block max-w-[90px] truncate" title={facilities.find(f => f.id === item.facility_id)?.name || 'Belirtilmemiş'}>
                        {facilities.find(f => f.id === item.facility_id)?.name || 'Belirtilmemiş'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {getStatusIcon(item.status)}
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${getStatusColor(item.status)}`}>
                          {getStatusText(item.status)}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {(isAdmin || isUserOwnItem(item)) && (
                        <button onClick={() => handleEdit(idx)} className="rounded-lg p-1.5 text-[#0047BB] hover:bg-[#0047BB]/10" title="Düzenle">
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {(isAdmin || isUserOwnItem(item)) && (
                        <button onClick={() => handleDelete(idx)} className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50" title="Sil">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {item.status !== 'Tamamlandı' && (isAdmin || isUserOwnItem(item)) && (
                        <button onClick={() => handleComplete(idx)} className="rounded-lg p-1.5 text-[#009CDE] hover:bg-[#009CDE]/10" title="Tamamla">
                          <CheckSquare className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 w-full max-w-2xl shadow-2xl border border-white/50">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {editIndex !== null ? 'İş Düzenle' : 'Yeni İş Ekle'}
                </h2>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">İş Adı *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    placeholder="İş adını girin..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Tarih *</label>
                  <input
                    type="date"
                    value={formData.completedDate}
                    onChange={e => setFormData({ ...formData, completedDate: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm resize-none"
                  placeholder="İş açıklaması..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Kullanıcı *</label>
                  <select
                    value={formData.user}
                    onChange={e => setFormData({ ...formData, user: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    required
                  >
                    <option value="">Kullanıcı Seçin</option>
                    {users.map(user => (
                      <option key={user.id} value={user.username}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-3">Tesis *</label>
                  <select
                    value={formData.facilityId}
                    onChange={e => setFormData({ ...formData, facilityId: e.target.value })}
                    className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    required
                  >
                    <option value="">Tesis Seçin</option>
                    {facilities.map(facility => (
                      <option key={facility.id} value={facility.id}>
                        {facility.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">Durum *</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                  required
                >
                  <option value="">Durum Seçin</option>
                  <option value="Tamamlandı">Tamamlandı</option>
                  <option value="İşlemde">İşlemde</option>
                  <option value="Beklemede">Beklemede</option>
                  <option value="Yapılmadı">Yapılmadı</option>
                </select>
              </div>
              <div className="flex gap-4 pt-6">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 px-6 rounded-2xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold text-lg"
                >
                  {editIndex !== null ? 'Güncelle' : 'Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 bg-slate-100 text-slate-700 py-4 px-6 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-bold text-lg"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Basit Tamamlama Modal */}
      {completeModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 w-full max-w-md shadow-2xl border border-white/50">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  İşi Tamamla
                </h2>
              </div>
              <button
                onClick={() => setCompleteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Yapılan İş *
                </label>
                <textarea
                  value={formData.workDone}
                  onChange={e => setFormData({...formData, workDone: e.target.value})}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-300 bg-white/80 backdrop-blur-sm resize-none"
                  placeholder="Yapılan işi detaylı olarak girin..."
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Tamamlanma Tarihi
                </label>
                <input
                  type="date"
                  value={formData.completionDate}
                  onChange={e => setFormData({...formData, completionDate: e.target.value})}
                  className="w-full border-2 border-slate-200 rounded-2xl px-4 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                />
              </div>
              <div className="flex gap-4 pt-6">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-4 px-6 rounded-2xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold text-lg"
                >
                  Tamamla
                </button>
                <button
                  type="button"
                  onClick={() => setCompleteModalOpen(false)}
                  className="flex-1 bg-slate-100 text-slate-700 py-4 px-6 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-bold text-lg"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Görüntüleme Modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 w-full max-w-2xl shadow-2xl border border-white/50 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {viewContent.title}
                </h2>
              </div>
              <button
                onClick={() => setViewModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-200">
              <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                {viewContent.content || 'İçerik bulunamadı'}
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setViewModalOpen(false)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-8 rounded-2xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyChecks; 